import { useState, useEffect, useMemo, useRef } from 'react';
import { BarChart3, Check, FileText, LineChart, Loader2, PieChart, Table2 } from 'lucide-react';
import { useChatStore } from '@/stores/chatStore';
import { useUIStore } from '@/stores/uiStore';
import Modal from '@/components/ui/Modal';
import FleetChart from './FleetChart';
import ChartFilters from './ChartFilters';
import type { ChartData } from '@/types/chart';
import { resolveChartPresentation } from '@/utils/chartPresentation';

type ChartVariantId = 'bar' | 'horizontal-bar' | 'line' | 'pie' | 'doughnut' | 'radar' | 'scatter' | 'table';

interface ChartVariant {
  id: ChartVariantId;
  label: string;
  title: string;
  type?: ChartData['type'];
  forceHorizontalBars?: boolean;
  forceVerticalBars?: boolean;
}

function buildPdfSubtitle(activeFilter?: string): string {
  return `Filtro activo: ${activeFilter || 'Sin filtro adicional'} | Generado: ${new Date().toLocaleString('es-MX')}`;
}

function getVisibleSeries(chart: ChartData, activeFilter?: string) {
  if (activeFilter && chart.filter_groups) {
    const group =
      chart.filter_groups[activeFilter] ||
      Object.entries(chart.filter_groups).find(
        ([key]) => key.toLowerCase() === activeFilter.toLowerCase()
      )?.[1];

    if (group) {
      return { labels: group.labels || [], values: group.values || [] };
    }
  }

  return {
    labels: chart.data.labels || [],
    values: chart.data.values || [],
  };
}

function hasCategoryValues(chart: ChartData, activeFilter?: string) {
  const { labels, values } = getVisibleSeries(chart, activeFilter);
  return labels.length > 0
    && values.length > 0
    && labels.length === values.length
    && values.some((value) => typeof value === 'number' && Number.isFinite(value));
}

function hasScatterPoints(chart: ChartData) {
  return !!chart.data.points?.length && chart.data.points.every((point) => (
    typeof point?.x === 'number'
    && Number.isFinite(point.x)
    && typeof point?.y === 'number'
    && Number.isFinite(point.y)
  ));
}

function getDefaultVariantId(chart: ChartData): ChartVariantId {
  const normalizedType = chart.type === 'radial' ? 'radar' : chart.type;
  const labelCount = chart.data.labels?.length || 0;

  if (normalizedType === 'bar' && labelCount > 18) return 'horizontal-bar';
  if (normalizedType === 'radar') return 'radar';
  if (normalizedType === 'scatter' && hasScatterPoints(chart)) return 'scatter';
  if (normalizedType === 'pie' || normalizedType === 'doughnut' || normalizedType === 'line') {
    return normalizedType;
  }
  if (hasCategoryValues(chart)) return 'bar';
  return 'table';
}

function buildChartVariants(chart: ChartData, activeFilter?: string): ChartVariant[] {
  const variants: ChartVariant[] = [];
  const canUseCategories = hasCategoryValues(chart, activeFilter);

  const addVariant = (variant: ChartVariant) => {
    if (!variants.some((item) => item.id === variant.id)) {
      variants.push(variant);
    }
  };

  if (canUseCategories) {
    addVariant({
      id: 'bar',
      label: 'Barras',
      title: 'Ver como barras verticales',
      type: 'bar',
      forceVerticalBars: true,
    });
    addVariant({
      id: 'horizontal-bar',
      label: 'Horizontal',
      title: 'Ver como barras horizontales',
      type: 'bar',
      forceHorizontalBars: true,
    });
    addVariant({ id: 'line', label: 'Línea', title: 'Ver como línea', type: 'line' });
    addVariant({ id: 'doughnut', label: 'Dona', title: 'Ver como dona', type: 'doughnut' });
    addVariant({ id: 'pie', label: 'Pastel', title: 'Ver como pastel', type: 'pie' });
    addVariant({ id: 'radar', label: 'Radar', title: 'Ver como radar', type: 'radar' });
  }

  if (hasScatterPoints(chart)) {
    addVariant({ id: 'scatter', label: 'Dispersión', title: 'Ver como dispersión', type: 'scatter' });
  }

  if (canUseCategories || hasScatterPoints(chart)) {
    addVariant({ id: 'table', label: 'Tabla', title: 'Ver datos en tabla' });
  }

  return variants;
}

function ChartVariantIcon({ variant }: { variant: ChartVariant }) {
  if (variant.id === 'table') return <Table2 size={14} />;
  if (variant.id === 'line') return <LineChart size={14} />;
  if (variant.id === 'pie' || variant.id === 'doughnut') return <PieChart size={14} />;
  if (variant.id === 'scatter') return <LineChart size={14} />;
  return <BarChart3 size={14} className={variant.id === 'horizontal-bar' ? 'rotate-90' : undefined} />;
}

function ChartVariantPreview({ variant }: { variant: Pick<ChartVariant, 'id' | 'label'> }) {
  if (variant.id === 'pie' || variant.id === 'doughnut') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="relative h-16 w-16 rounded-full bg-[conic-gradient(#3b82f6_0_38%,#22c55e_38%_60%,#f97316_60%_78%,#a855f7_78%_100%)]">
          {variant.id === 'doughnut' && (
            <div className="absolute inset-4 rounded-full bg-bg-primary" />
          )}
        </div>
      </div>
    );
  }

  if (variant.id === 'line' || variant.id === 'scatter') {
    return (
      <div className="flex h-full items-center justify-center px-5">
        <div className="relative h-16 w-full">
          <div className="absolute inset-x-0 bottom-1 h-px bg-border-subtle" />
          <div className="absolute inset-y-0 left-1 w-px bg-border-subtle" />
          {variant.id === 'line' ? (
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 160 64" role="presentation">
              <polyline
                points="8,52 42,38 76,44 112,18 152,26"
                fill="none"
                stroke="#2563eb"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <div className="absolute inset-0">
              {[
                ['10%', '70%'],
                ['28%', '52%'],
                ['44%', '60%'],
                ['60%', '34%'],
                ['78%', '28%'],
                ['90%', '18%'],
              ].map(([left, top]) => (
                <span
                  key={`${left}-${top}`}
                  className="absolute h-2.5 w-2.5 rounded-full bg-accent-blue"
                  style={{ left, top }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant.id === 'radar') {
    return (
      <div className="flex h-full items-center justify-center">
        <svg className="h-20 w-20 text-accent-blue" viewBox="0 0 90 90" role="presentation">
          <polygon points="45,8 80,32 67,76 23,76 10,32" fill="none" stroke="#dbe5f3" strokeWidth="2" />
          <polygon points="45,20 68,36 60,64 30,64 22,36" fill="none" stroke="#dbe5f3" strokeWidth="2" />
          <polygon points="45,18 64,40 56,58 34,68 24,34" fill="#2563eb" fillOpacity="0.2" stroke="#2563eb" strokeWidth="3" />
        </svg>
      </div>
    );
  }

  const horizontal = variant.id === 'horizontal-bar';
  const bars = [72, 54, 42, 30, 18];
  return (
    <div className={`flex h-full items-end justify-center gap-2 p-5 ${horizontal ? 'flex-col items-start' : ''}`}>
      {bars.map((size, index) => (
        <span
          key={size}
          className="rounded-sm"
          style={{
            backgroundColor: ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#14b8a6'][index],
            height: horizontal ? 8 : size,
            width: horizontal ? `${size}%` : 12,
          }}
        />
      ))}
    </div>
  );
}

function ChartDataTable({ chart, activeFilter }: { chart: ChartData; activeFilter?: string }) {
  const { labels, values } = getVisibleSeries(chart, activeFilter);
  const points = chart.data.points || [];
  const presentation = resolveChartPresentation(chart, activeFilter);

  if (labels.length && values.length && labels.length === values.length) {
    return (
      <div className="h-full overflow-auto rounded-xl border border-border-subtle bg-bg-primary">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-bg-secondary text-text-muted">
            <tr>
              <th className="px-4 py-3 font-black uppercase text-[11px] tracking-wide">{presentation.categoryHeader}</th>
              <th className="px-4 py-3 font-black uppercase text-[11px] tracking-wide text-right">{presentation.valueHeader}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {labels.map((label, index) => (
              <tr key={`${label}-${index}`} className="hover:bg-bg-secondary/70">
                <td className="px-4 py-3 text-text-primary font-semibold">{label}</td>
                <td className="px-4 py-3 text-text-secondary text-right font-mono">
                  {Number(values[index] || 0).toLocaleString('es-MX')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (points.length) {
    return (
      <div className="h-full overflow-auto rounded-xl border border-border-subtle bg-bg-primary">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 bg-bg-secondary text-text-muted">
            <tr>
              <th className="px-4 py-3 font-black uppercase text-[11px] tracking-wide">{presentation.scatterXHeader}</th>
              <th className="px-4 py-3 font-black uppercase text-[11px] tracking-wide">{presentation.scatterYHeader}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {points.map((point, index) => (
              <tr key={`${point.x}-${point.y}-${index}`} className="hover:bg-bg-secondary/70">
                <td className="px-4 py-3 text-text-primary font-mono">{point.x}</td>
                <td className="px-4 py-3 text-text-secondary font-mono">{point.y}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

function buildTablePdfPayload(chart: ChartData, activeFilter: string | undefined, presentation: ReturnType<typeof resolveChartPresentation>) {
  const { labels, values } = getVisibleSeries(chart, activeFilter);
  const points = chart.data.points || [];

  if (labels.length && values.length && labels.length === values.length) {
    return {
      headers: [presentation.categoryHeader, presentation.valueHeader],
      rows: labels.map((label, index) => [
        String(label),
        Number(values[index] || 0).toLocaleString('es-MX'),
      ]),
    };
  }

  if (points.length) {
    return {
      headers: [presentation.scatterXHeader, presentation.scatterYHeader],
      rows: points.map((point) => [
        String(point.x),
        String(point.y),
      ]),
    };
  }

  return { headers: [], rows: [] };
}

function captureChartCanvasAsDataUrl(container: HTMLDivElement | null): string | null {
  const canvas = container?.querySelector('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    return null;
  }

  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = canvas.width;
  exportCanvas.height = canvas.height;
  const ctx = exportCanvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
  ctx.drawImage(canvas, 0, 0);
  return exportCanvas.toDataURL('image/png', 1.0);
}

export default function ChartModal() {
  const chartModalOpen = useUIStore((s) => s.chartModalOpen);
  const setChartModalOpen = useUIStore((s) => s.setChartModalOpen);
  const currentChart = useChatStore((s) => s.currentChart);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [activeVariantId, setActiveVariantId] = useState<ChartVariantId>('bar');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfSelectionOpen, setPdfSelectionOpen] = useState(false);
  const [selectedPdfViewIds, setSelectedPdfViewIds] = useState<string[]>([]);
  const hiddenVariantRefs = useRef<Partial<Record<ChartVariantId, HTMLDivElement | null>>>({});

  // Reset filter when chart changes to prevent stale state
  // (e.g. "Veracruz" from radar leaking into customer bar chart)
  useEffect(() => {
    setActiveFilter(undefined);
    setActiveVariantId(currentChart ? getDefaultVariantId(currentChart) : 'bar');
    setPdfSelectionOpen(false);
  }, [currentChart]);

  const variants = useMemo(
    () => currentChart ? buildChartVariants(currentChart, activeFilter) : [],
    [currentChart, activeFilter]
  );

  useEffect(() => {
    if (variants.length && !variants.some((variant) => variant.id === activeVariantId)) {
      setActiveVariantId(variants[0].id);
    }
  }, [activeVariantId, variants]);

  const chartVariantsForPdf = useMemo(
    () => variants.filter((variant) => variant.id !== 'table'),
    [variants]
  );
  const pdfSelectionOptions = useMemo(
    () => [
      ...chartVariantsForPdf.map((variant) => ({
        id: variant.id,
        label: variant.label,
      })),
      ...(variants.some((variant) => variant.id === 'table')
        ? [{ id: 'table', label: 'Tabla de datos' }]
        : []),
    ],
    [chartVariantsForPdf, variants]
  );
  const pdfSelectionOptionIds = useMemo(
    () => pdfSelectionOptions.map((option) => option.id),
    [pdfSelectionOptions]
  );
  const pdfSelectionOptionKey = pdfSelectionOptionIds.join('|');

  useEffect(() => {
    setSelectedPdfViewIds(pdfSelectionOptionIds);
  }, [pdfSelectionOptionKey]);

  if (!currentChart) return null;

  const activeVariant = variants.find((variant) => variant.id === activeVariantId)
    || variants[0]
    || {
      id: getDefaultVariantId(currentChart),
      label: '',
      title: '',
      type: currentChart.type,
    };

  const presentation = resolveChartPresentation(currentChart, activeFilter);
  const displayTitle = presentation.displayTitle;
  const tablePayload = buildTablePdfPayload(currentChart, activeFilter, presentation);
  const selectedPdfChartVariants = chartVariantsForPdf.filter((variant) => (
    selectedPdfViewIds.includes(variant.id)
  ));
  const includePdfTable = selectedPdfViewIds.includes('table');

  const togglePdfView = (viewId: string) => {
    setSelectedPdfViewIds((current) => (
      current.includes(viewId)
        ? current.filter((id) => id !== viewId)
        : [...current, viewId]
    ));
  };

  const handleExportPdf = async () => {
    if (isExportingPdf || selectedPdfViewIds.length === 0) return;
    setIsExportingPdf(true);
    try {
      const subtitle = buildPdfSubtitle(activeFilter);
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      const pages = selectedPdfChartVariants
        .map((variant) => {
          const imageData = captureChartCanvasAsDataUrl(hiddenVariantRefs.current[variant.id] || null);
          if (!imageData) {
            return null;
          }
          return {
            label: variant.label,
            imageData,
          };
        })
        .filter((page): page is { label: string; imageData: string } => !!page);
      const { exportChartVariantsPdf } = await import('@/utils/chartPdf');

      await exportChartVariantsPdf({
        title: displayTitle,
        subtitle,
        pages,
        table: includePdfTable && tablePayload.rows.length
          ? {
              title: 'Tabla de datos',
              headers: tablePayload.headers,
              rows: tablePayload.rows,
            }
          : null,
      });
      setPdfSelectionOpen(false);
    } catch (error) {
      console.error('Chart PDF export failed:', error);
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <Modal
      open={chartModalOpen}
      onClose={() => setChartModalOpen(false)}
      className="w-[98vw] max-w-[1800px] h-[94vh] max-h-none p-6 sm:p-8"
    >
      <div className="mb-3 flex shrink-0 items-center justify-between gap-4">
        <h2 className="min-w-0 truncate text-lg font-bold text-accent-blue">{displayTitle}</h2>
        <button
          onClick={() => setChartModalOpen(false)}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl font-bold text-text-muted transition-colors hover:bg-bg-secondary hover:text-text-primary"
        >
          &times;
        </button>
      </div>

      <ChartFilters key={currentChart.title} chart={currentChart} onFilterChange={setActiveFilter} />

      {variants.length > 0 && (
        <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 rounded-xl border border-border-subtle bg-bg-secondary/40 p-1">
              {variants.map((variant) => {
                const isActive = variant.id === activeVariant.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    title={variant.title}
                    onClick={() => setActiveVariantId(variant.id)}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-black transition-all active:scale-95 ${
                      isActive
                        ? 'border-accent-blue bg-bg-primary text-accent-blue shadow-sm'
                        : 'border-transparent text-text-secondary hover:bg-bg-primary hover:text-text-primary'
                    }`}
                  >
                    <ChartVariantIcon variant={variant} />
                    <span>{variant.label}</span>
                  </button>
                );
              })}
          </div>

          <div className="relative flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPdfSelectionOpen(true)}
                disabled={isExportingPdf || pdfSelectionOptions.length === 0}
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-accent-blue/30 bg-accent-blue/10 px-2.5 text-[11px] font-black text-accent-blue transition-all hover:bg-accent-blue/15 disabled:cursor-not-allowed disabled:opacity-60 active:scale-95"
              >
                {isExportingPdf ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                <span>{isExportingPdf ? 'Exportando' : 'PDF'}</span>
              </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 relative overflow-hidden">
        {activeVariant.id === 'table' ? (
          <ChartDataTable chart={currentChart} activeFilter={activeFilter} />
        ) : (
          <FleetChart
            key={`${currentChart.title}-${currentChart.data.labels.join(',')}-${activeVariant.id}`}
            chart={currentChart}
            activeFilter={activeFilter}
            typeOverride={activeVariant.type}
            forceHorizontalBars={activeVariant.forceHorizontalBars}
            forceVerticalBars={activeVariant.forceVerticalBars}
          />
        )}
      </div>

      <div
        aria-hidden="true"
        className="pointer-events-none fixed -left-[200vw] top-0 h-0 w-0 overflow-hidden opacity-0"
      >
        {chartVariantsForPdf.map((variant) => (
          <div
            key={`pdf-${variant.id}`}
            ref={(node) => {
              hiddenVariantRefs.current[variant.id] = node;
            }}
            className="h-[760px] w-[1200px] bg-white p-6"
          >
            <FleetChart
              key={`pdf-chart-${currentChart.title}-${variant.id}-${activeFilter || 'all'}`}
              chart={currentChart}
              activeFilter={activeFilter}
              typeOverride={variant.type}
              forceHorizontalBars={variant.forceHorizontalBars}
              forceVerticalBars={variant.forceVerticalBars}
            />
          </div>
        ))}
      </div>

      {pdfSelectionOpen && (
        <div
          className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
          onClick={(event) => {
            if (event.target === event.currentTarget && !isExportingPdf) {
              setPdfSelectionOpen(false);
            }
          }}
        >
          <div className="flex max-h-[86vh] w-full max-w-5xl flex-col rounded-2xl border border-border-subtle bg-bg-primary shadow-2xl">
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border-subtle px-5 py-4">
              <div className="min-w-0">
                <h3 className="truncate text-base font-black text-text-primary">Exportar PDF</h3>
                <p className="mt-0.5 text-xs text-text-muted">
                  Selecciona las vistas que quieres incluir.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPdfSelectionOpen(false)}
                disabled={isExportingPdf}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl font-bold text-text-muted transition-colors hover:bg-bg-secondary hover:text-text-primary disabled:opacity-50"
              >
                &times;
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className="text-xs font-bold text-text-muted">
                  {selectedPdfViewIds.length} de {pdfSelectionOptions.length} seleccionadas
                </span>
                <div className="flex items-center gap-3 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setSelectedPdfViewIds(pdfSelectionOptions.map((option) => option.id))}
                    className="text-accent-blue hover:text-accent-blue/80"
                  >
                    Todas
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedPdfViewIds([])}
                    className="text-text-muted hover:text-text-primary"
                  >
                    Ninguna
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {chartVariantsForPdf.map((variant) => {
                  const checked = selectedPdfViewIds.includes(variant.id);
                  return (
                    <button
                      key={`select-${variant.id}`}
                      type="button"
                      onClick={() => togglePdfView(variant.id)}
                      className={`group overflow-hidden rounded-xl border bg-bg-primary text-left transition-all ${
                        checked
                          ? 'border-accent-blue shadow-sm shadow-accent-blue/10'
                          : 'border-border-subtle hover:border-accent-blue/35'
                      }`}
                    >
                      <div className="h-28 border-b border-border-subtle bg-bg-secondary/35">
                        <ChartVariantPreview variant={variant} />
                      </div>
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <span className="inline-flex items-center gap-2 text-xs font-black text-text-primary">
                          <ChartVariantIcon variant={variant} />
                          {variant.label}
                        </span>
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md border ${
                          checked ? 'border-accent-blue bg-accent-blue text-white' : 'border-border-subtle bg-bg-secondary'
                        }`}>
                          {checked ? <Check size={13} /> : null}
                        </span>
                      </div>
                    </button>
                  );
                })}

                {variants.some((variant) => variant.id === 'table') && (
                  <button
                    type="button"
                    onClick={() => togglePdfView('table')}
                    className={`group overflow-hidden rounded-xl border bg-bg-primary text-left transition-all ${
                      includePdfTable
                        ? 'border-accent-blue shadow-sm shadow-accent-blue/10'
                        : 'border-border-subtle hover:border-accent-blue/35'
                    }`}
                  >
                    <div className="h-28 border-b border-border-subtle bg-bg-secondary/35 p-5">
                      <div className="h-full overflow-hidden rounded-lg border border-border-subtle bg-bg-primary">
                        <div className="grid grid-cols-2 bg-bg-secondary px-2 py-1.5 text-[10px] font-black text-text-muted">
                          <span>Campo</span>
                          <span>Valor</span>
                        </div>
                        {[1, 2, 3].map((row) => (
                          <div key={row} className="grid grid-cols-2 border-t border-border-subtle px-2 py-1.5">
                            <span className="h-2 rounded bg-border-subtle" />
                            <span className="ml-auto h-2 w-10 rounded bg-border-subtle" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <span className="inline-flex items-center gap-2 text-xs font-black text-text-primary">
                        <Table2 size={14} />
                        Tabla de datos
                      </span>
                      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md border ${
                        includePdfTable ? 'border-accent-blue bg-accent-blue text-white' : 'border-border-subtle bg-bg-secondary'
                      }`}>
                        {includePdfTable ? <Check size={13} /> : null}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-5 py-4">
              <button
                type="button"
                onClick={() => setPdfSelectionOpen(false)}
                disabled={isExportingPdf}
                className="inline-flex h-9 items-center justify-center rounded-lg border border-border-subtle bg-bg-primary px-4 text-xs font-black text-text-secondary transition-colors hover:bg-bg-secondary hover:text-text-primary disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={isExportingPdf || selectedPdfViewIds.length === 0}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-accent-blue bg-accent-blue px-4 text-xs font-black text-white transition-colors hover:bg-accent-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isExportingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {isExportingPdf ? 'Exportando' : `Exportar ${selectedPdfViewIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
